import * as React from "react";
import SberLogo from "@/assets/icons/banks/sber.svg?react";
import TbankLogo from "@/assets/icons/banks/tbank.svg?react";
import VtbLogo from "@/assets/icons/banks/vtb.svg?react";
import Typography from "@/shared/ui/Typography";
import { InputRange } from "@/shared/ui/inputs/InputRange";
import { BankCard } from "@/shared/ui/cards/BankCard";
import { SectionLayout } from "@/shared/ui/layouts/SectionLayout";
import SmallButton from "@/shared/ui/buttons/SmallButton";

const backIconMap = {
    sber: <SberLogo className="w-fit h-[21px]" />,
    vtb: <VtbLogo className="w-fit h-[21px]" />,
    tbank: <TbankLogo className="w-fit h-[21px]" />,
};

export type MortgageProgram = {
  id: string;
  label: string;
  rate: number;
};

type CalculateDataType = {
    price: number;
    down_payment: number;
    expiration_years: number;
};

type BankType = {
    id: string;
    name: string;
    interest_rate: number;
    total: number;
};

const mortgageData = {
        banks: [
            { id: "sber", name: "Сбербанк", interest_rate: 12 },
            { id: "vtb", name: "ВТБ", interest_rate: 11 },
            { id: "tbank", name: "Т-Банк", interest_rate: 13 }
        ],
        programs: [
            { id: "standard", label: "Стандартный", rate: 12 },
            { id: "support", label: "Гос.поддержка", rate: 8 },
            { id: "military", label: "Военная", rate: 6 },
            { id: "it", label: "IT", rate: 5 },
            { id: "far_east", label: "Дальневосточная", rate: 7 },
            { id: "arctic", label: "Арктическая", rate: 9 },
            { id: "village", label: "Сельская", rate: 4 }
        ]
    }

export function MortgageCalculator() {

    const [selectedProgram, setSelectedProgram] =
        React.useState<string>("standard");
    const [calculateData, setCalculateData] = React.useState<CalculateDataType>(
        {
            price: 10_000_000,
            down_payment: 1_000_000,
            expiration_years: 20,
        }
    );
    const [banks, setBanks] = React.useState<BankType[]>([]);

    const programs: MortgageProgram[] = mortgageData.programs;

    const calculateMonthlyPayment = (
        price: number,
        down: number,
        years: number,
        rate: number
    ) => {
        const months = years * 12;
        const loanAmount = price - down;
        const monthlyRate = rate / 100 / 12;
        const payment =
            (loanAmount * monthlyRate) /
            (1 - Math.pow(1 + monthlyRate, -months));
        return Math.round(payment);
    };

    React.useEffect(() => {
        if (!mortgageData?.banks) return;

        const initialBanks = mortgageData.banks.map((bank) => ({
            ...bank,
            total: 0,
        }));

        setBanks(initialBanks);
    }, [mortgageData.banks]);

    React.useEffect(() => {
        if (!mortgageData?.banks || !programs.length) return;

        const currentProgram = programs.find((p) => p.id === selectedProgram);
        if (!currentProgram) return;

        const newBanks = mortgageData.banks.map((bank) => ({
            ...bank,
            interest_rate: currentProgram.rate,
            total: calculateMonthlyPayment(
                calculateData.price,
                calculateData.down_payment,
                calculateData.expiration_years,
                currentProgram.rate
            ),
        }));

        setBanks(newBanks);
    }, [calculateData, selectedProgram, mortgageData?.banks, programs]);

    return (
        <SectionLayout containerClassName="container pt-[40px] pb-[20px]">
            <div className="flex flex-col gap-[28px] md:gap-[32px]">
                <Typography className="uppercase text-[20px] font-bold md:text-[26px] !leading-[120%]">
                    Воспользуйтесь нашим ипотечным калькулятором
                </Typography>

                <div className="flex flex-col gap-[40px] lg:flex-row lg:items-start">
                    <div className="flex flex-col gap-[24px] md:gap-[32px] lg:gap-[44px] lg:w-[326px] xl:w-[526px]">
                        <InputRange
                            label="Стоимость недвижимости"
                            value={calculateData.price}
                            min={1_000_000}
                            max={50_000_000}
                            unit="₽"
                            onChange={(value) =>
                                setCalculateData((prev) => ({
                                    ...prev,
                                    price: value,
                                }))
                            }
                        />
                        <InputRange
                            label="Первоначальный взнос"
                            value={calculateData.down_payment}
                            min={200_000}
                            max={10_000_000}
                            unit="₽"
                            onChange={(value) =>
                                setCalculateData((prev) => ({
                                    ...prev,
                                    down_payment: value,
                                }))
                            }
                        />
                        <InputRange
                            label="Срок кредита"
                            value={calculateData.expiration_years}
                            min={5}
                            max={30}
                            unit="лет"
                            onChange={(value) =>
                                setCalculateData((prev) => ({
                                    ...prev,
                                    expiration_years: value,
                                }))
                            }
                        />
                    </div>

                    <div className="flex flex-col gap-[10px] lg:w-auto lg:flex-1">
                        <div className="flex flex-wrap gap-[4px] items-center">
                            {programs.map((prog) => (

                                <SmallButton
                                    key={prog.id}
                                    onClick={() => setSelectedProgram(prog.id)}
                                    active={selectedProgram === prog.id}
                                >
                                    {prog.label}
                                </SmallButton>
                            ))}
                        </div>

                        <div className="flex flex-col gap-[4px] md:gap-[10px] py-2 h-[293px] md:h-[369px] overflow-y-auto">
                            {banks.map((bank) => (
                                <BankCard
                                    key={bank.id}
                                    icon={
                                        backIconMap[
                                            bank.id as keyof typeof backIconMap
                                        ]
                                    }
                                    type={
                                        programs.find(
                                            (p) => p.id === selectedProgram
                                        )?.label || ""
                                    }
                                    interest_rate={bank.interest_rate}
                                    total={`${bank.total.toLocaleString()} ₽/мес`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </SectionLayout>
    );
}
